import { Link } from 'react-router-dom';
import { useIslands, useClinics } from '../hooks/useApi';
import {
  MapPin, ArrowRight, Check, Stethoscope,
  Globe, Smartphone, Users, Clock, Building2, Leaf, Baby,
} from 'lucide-react';

export default function HomePage() {
  const { data: islands } = useIslands();
  const { data: clinicsData } = useClinics(undefined, 1, 1);

  const clinicCount = clinicsData?.total ?? 78;
  const islandCount = islands?.length ?? 17;

  return (
    <div>
      {/* Hero */}
      <section className="px-4 py-16 md:py-24">
        <div className="max-w-4xl mx-auto">
          <span className="inline-block bg-primary-50 text-primary text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
            Ministry of Health &amp; Wellness
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-navy leading-tight">
            The Future of<br />Island Health<br />
            <span className="text-primary">Is Here.</span>
          </h1>
          <p className="text-gray-500 text-lg md:text-xl mt-6 max-w-xl leading-relaxed">
            Connecting every Bahamian to comprehensive healthcare across {islandCount} islands.
            Find clinics, book appointments, check symptoms — all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/clinics" className="btn btn-primary btn-lg">
              Book Appointment
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/clinics" className="btn btn-outline btn-lg">
              <MapPin className="w-5 h-5" />
              Find Local Clinic
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-4 py-12">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { value: clinicCount.toString(), label: 'Clinics', icon: Building2 },
            { value: islandCount.toString(), label: 'Islands', icon: Globe },
            { value: '120k+', label: 'Patients', icon: Users },
            { value: '24/7', label: 'Telehealth', icon: Clock },
          ].map((stat) => (
            <div key={stat.label} className="card text-center">
              <stat.icon className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-2xl md:text-3xl font-extrabold text-navy">{stat.value}</p>
              <p className="text-sm text-gray-400 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Network */}
      <section className="px-4 py-16">
        <div className="max-w-5xl mx-auto md:flex md:items-center md:gap-16">
          <div className="flex-1 mb-8 md:mb-0">
            <h2 className="text-3xl md:text-4xl font-extrabold text-navy leading-tight">
              Every Island.<br />One Network.
            </h2>
            <p className="text-gray-500 mt-4 leading-relaxed">
              From Nassau to the Family Islands, our integrated healthcare platform ensures
              every Bahamian has access to quality medical services regardless of location.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                'Centralized Digital Health Records',
                'Specialist Referral Network',
                'Remote Telehealth Consultations',
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-primary-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-primary" />
                  </span>
                  <span className="text-navy font-medium text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/clinics" className="btn btn-primary mt-8">
              Explore Clinics
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex-1">
            <div className="bg-gradient-to-br from-primary-50 to-white rounded-2xl p-8 text-center">
              <Globe className="w-16 h-16 text-primary mx-auto mb-4" />
              <p className="text-4xl font-extrabold text-navy">{islandCount} Islands</p>
              <p className="text-gray-400 mt-2">Connected Healthcare Network</p>
            </div>
          </div>
        </div>
      </section>

      {/* Clinics by Island */}
      <section className="px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-extrabold text-navy text-center mb-8">Clinics by Island</h2>
          <div className="flex flex-wrap justify-center gap-2">
            {islands?.map((island) => (
              <Link
                key={island.island}
                to={`/clinics?island=${encodeURIComponent(island.island)}`}
                className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-600 hover:border-primary hover:text-primary transition-colors"
              >
                {island.island} ({island.clinic_count})
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Wellness Programs */}
      <section className="px-4 py-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-extrabold text-navy text-center mb-3">Wellness Programs</h2>
          <p className="text-gray-400 text-center mb-10">
            Comprehensive health initiatives designed for island living
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Leaf,
                title: 'Active Islands',
                desc: 'Community fitness programs for all islands — walking groups, beach wellness, and guided exercise.',
              },
              {
                icon: Stethoscope,
                title: 'Island Nutrition',
                desc: 'Nutrition guidance, local eating plans, healthy recipes, and traditional food wellness.',
              },
              {
                icon: Baby,
                title: 'Maternal Care+',
                desc: 'Comprehensive prenatal care, traditional midwife consultations, and postnatal support.',
              },
            ].map((prog) => (
              <div key={prog.title} className="card hover:shadow-md transition-shadow">
                <span className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center mb-4">
                  <prog.icon className="w-5 h-5 text-primary" />
                </span>
                <h3 className="text-navy font-bold text-lg">{prog.title}</h3>
                <p className="text-gray-500 text-sm mt-2 leading-relaxed">{prog.desc}</p>
                <Link to="/services" className="text-primary text-sm font-semibold mt-4 hover:underline inline-block">
                  Learn More &rarr;
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="px-4 py-12">
        <div className="max-w-3xl mx-auto card">
          <p className="text-navy text-lg md:text-xl font-medium leading-relaxed italic">
            &ldquo;Clinic Connect transformed how our family accesses healthcare. From Marsh
            Harbour, I can now book specialist appointments in Nassau and manage all our
            health records digitally.&rdquo;
          </p>
          <div className="mt-6 flex items-center justify-between">
            <div>
              <p className="text-navy font-bold">Roland Thompson</p>
              <p className="text-gray-400 text-sm">Marsh Harbour Resident</p>
            </div>
            <div className="flex gap-6 text-center">
              <div>
                <p className="text-2xl font-extrabold text-primary">94%</p>
                <p className="text-xs text-gray-400">Satisfaction</p>
              </div>
              <div>
                <p className="text-2xl font-extrabold text-primary">45%</p>
                <p className="text-xs text-gray-400">Reduced Wait</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Health */}
      <section className="px-4 py-16">
        <div className="max-w-4xl mx-auto md:flex md:items-center md:gap-12">
          <div className="flex-1 mb-8 md:mb-0">
            <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Mobile Care</span>
            <h2 className="text-3xl font-extrabold text-navy mt-2 leading-tight">
              Care That Moves<br />With You.
            </h2>
            <p className="text-gray-500 mt-4 leading-relaxed">
              Our mobile health units cover communities across the Family Islands, providing
              essential health screenings, vaccinations, and primary care to neighborhoods
              that need them most.
            </p>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-500 text-sm font-medium rounded-lg mt-6">
              <Smartphone className="w-4 h-4" />
              Coming Soon
            </span>
          </div>
          <div className="flex-1">
            <div className="bg-gradient-to-br from-accent/10 to-cream rounded-2xl p-8 text-center">
              <Smartphone className="w-16 h-16 text-accent mx-auto mb-4" />
              <p className="text-2xl font-extrabold text-navy">Mobile Health Unit</p>
              <p className="text-gray-400 mt-2">Serving Family Islands Weekly</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-16">
        <div className="max-w-2xl mx-auto text-center bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-10 md:p-16 text-white">
          <h2 className="text-2xl md:text-3xl font-extrabold">
            Ready to find your<br />nearest clinic?
          </h2>
          <p className="text-white/70 mt-4">
            Whether you need a routine check-up or urgent care guidance, we&apos;re here to
            help you find the right care.
          </p>
          <Link to="/clinics" className="btn bg-white text-primary hover:bg-gray-100 btn-lg mt-8 font-bold">
            Get Started
          </Link>
        </div>
      </section>
    </div>
  );
}
